import {
  Activity,
  BarChart3,
  Bell,
  Boxes,
  ChevronDown,
  CircleDollarSign,
  Columns2,
  Contact,
  Database,
  Ellipsis,
  FileSpreadsheet,
  Gauge,
  Home,
  LineChart,
  Package,
  Radio,
  Settings,
  ShoppingCart,
  Target,
  TrendingUp,
  Users,
  Workflow,
} from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { WorkspaceSwitcher } from '@/features/workspace/components/workspace-switcher/workspace-switcher';
import { ApiKeysLink } from '@/app/shell/api-keys-link';
import {
  OverviewSavedViewsSection,
  OverviewSummaryPin,
  OverviewViewsHub,
} from '@/features/dashboard/components/overview-views-subnav';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  getDashboardNavZones,
  getNonDashboardItems,
  SETUP_ITEMS,
} from '@/app/shell/helpers/sidebar-nav';
import {
  REPORTS_MORE_ITEM_NAME,
  REPORTS_SECTION_TITLE,
  SYSTEM_SECTION_TITLE,
} from '@/lib/navigation/constants/navigation-constants';
import type { LegacyNavItem, NavStatusSnapshot } from '@/lib/navigation/types/nav-types';
import { NAVIGATION_LIST_ITEM_STATE, NAVIGATION_LIST_ROOT_AS } from '@/components/ui/types/navigation-list-variants-enums';
import { NavigationList } from '@/components/ui/navigation-list';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Sidebar, SidebarRail } from '@/components/ui/sidebar/sidebar-core';
import { SidebarContent, SidebarFooter, SidebarHeader } from '@/components/ui/sidebar/sidebar-layout';
import { useSidebar } from '@/components/ui/sidebar/hooks/use-sidebar';
import { getSidebarNavLeafItemClassName, sidebarChevron, sidebarContent, sidebarFooter, sidebarHeader, sidebarIcon, sidebarIconButton, sidebarNestedContainer, sidebarSection, sidebarSectionHeading, sidebarStatusIndicator } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { SIDEBAR_COLLAPSIBLE } from '@/components/ui/sidebar/types/sidebar-enums';
import { SIDEBAR_NAV_ITEM_LAYOUT, SIDEBAR_NAV_ITEM_STATE } from '@/components/ui/sidebar/types/sidebar-theme-enums';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/browser/use-is-mobile';
import { useHasShopifyData } from '@/features/shopify/hooks/use-has-shopify-data';
import {
  NAV_STATUS_KEY,
  NAV_STATUS_VARIANT,
} from '@/lib/navigation/types/nav-enums';
import { isNavLinkActive } from '@/lib/navigation/nav-match';
import { formatUnreadCountLabel, getStatusAccessibleLabel } from '@/features/workspace/helpers/nav-status';
import { useNavStatusSnapshot } from '@/app/shell/hooks/use-nav-status-snapshot';
import { cn } from '@/lib/utils';
import { useAccountState, useCurrentAccount } from '@/contexts/account/account-context';

function getProductsZoneLeading(item: LegacyNavItem): ReactNode {
  const link = item.link ?? '';
  if (item.name === REPORTS_MORE_ITEM_NAME || !item.link) {
    return <Ellipsis className={sidebarIcon()} aria-hidden />;
  }
  if (link === '/board/pnl/dashboard' || link.startsWith('/board/pnl/dashboard')) {
    return <CircleDollarSign className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/pnl/income-statement')) {
    return <FileSpreadsheet className={sidebarIcon()} aria-hidden />;
  }
  if (link === '/board/realtime' || link.startsWith('/board/realtime')) {
    return <Radio className={sidebarIcon()} aria-hidden />;
  }
  if (link === '/board/reach/rolling') {
    return <Target className={sidebarIcon()} aria-hidden />;
  }
  if (link === '/board/reach/comparison') {
    return <Columns2 className={sidebarIcon()} aria-hidden />;
  }
  if (link === '/board/reach/incremental') {
    return <TrendingUp className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/reach')) {
    return <Target className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/traffic')) {
    return <LineChart className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/cohorts')) {
    return <Users className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/ecommerce')) {
    return <ShoppingCart className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/kpis')) {
    return <Gauge className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/orders')) {
    return <Package className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/board/products')) {
    return <Boxes className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/meta-data-quality')) {
    return <BarChart3 className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/events-manager')) {
    return <Activity className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/destinations')) {
    return <Workflow className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/integrations')) {
    return <Database className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/notifications')) {
    return <Bell className={sidebarIcon()} aria-hidden />;
  }
  if (link.startsWith('/account/acquisition')) {
    return <Contact className={sidebarIcon()} aria-hidden />;
  }
  return null;
}

function getNavigationListState(isActive: boolean): NAVIGATION_LIST_ITEM_STATE {
  return isActive ? NAVIGATION_LIST_ITEM_STATE.ACTIVE : NAVIGATION_LIST_ITEM_STATE.DEFAULT;
}

function getSidebarThemeState(isActive: boolean): SIDEBAR_NAV_ITEM_STATE {
  return isActive ? SIDEBAR_NAV_ITEM_STATE.ACTIVE : SIDEBAR_NAV_ITEM_STATE.DEFAULT;
}

function getStatusIndicatorClassName(variant: NAV_STATUS_VARIANT): string {
  switch (variant) {
    case NAV_STATUS_VARIANT.SUCCESS:
      return sidebarStatusIndicator({ status: 'success' });
    case NAV_STATUS_VARIANT.WARNING:
      return sidebarStatusIndicator({ status: 'warning' });
    case NAV_STATUS_VARIANT.ERROR:
      return sidebarStatusIndicator({ status: 'error' });
    case NAV_STATUS_VARIANT.PENDING:
      return sidebarStatusIndicator({ status: 'pending' });
  }
}

function resolveItemStatus(
  item: LegacyNavItem,
  statusSnapshot?: NavStatusSnapshot
): NAV_STATUS_VARIANT | undefined {
  if (!item.hasStatus || !statusSnapshot) return undefined;

  const key = item.statusKey;
  if (key) return statusSnapshot.variants[key];

  if (item.name === 'Data Sources') return statusSnapshot.variants[NAV_STATUS_KEY.INTEGRATION_HEALTH];
  if (item.name === 'Destinations') return statusSnapshot.variants[NAV_STATUS_KEY.DESTINATION_HEALTH];
  if (item.name === 'Events Manager') {
    return statusSnapshot.variants[NAV_STATUS_KEY.EVENT_HEALTH];
  }
  if (item.name === 'Meta EMQ') {
    return statusSnapshot.variants[NAV_STATUS_KEY.EMQ_HEALTH];
  }

  return NAV_STATUS_VARIANT.SUCCESS;
}

function AccessorySlot({ children }: { children?: ReactNode }) {
  if (!children) return null;

  return <span className="flex shrink-0 items-center justify-center gap-1">{children}</span>;
}

function NavLabel({ children }: { children: ReactNode }) {
  return <span className="min-w-0 flex-1 truncate text-left">{children}</span>;
}

function StatusDot({ variant, large = false }: { variant: NAV_STATUS_VARIANT; large?: boolean }) {
  const label = getStatusAccessibleLabel(variant);

  return (
    <span className="flex items-center gap-1">
      <span className={cn(large && 'h-2 w-2 shrink-0', getStatusIndicatorClassName(variant))} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

function NotificationsBadge({ count, dot = false }: { count: number; dot?: boolean }) {
  if (count <= 0) return null;

  const label = formatUnreadCountLabel(count);

  if (dot) {
    return <span aria-label={label} className="h-2 w-2 shrink-0 rounded-full bg-action-primary" />;
  }

  return (
    <span
      aria-label={label}
      className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-action-primary px-1 type-settings-row-meta font-medium leading-none tabular-nums text-action-primary-foreground"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function NavItemAccessory({
  dot,
  item,
  statusSnapshot,
}: {
  dot?: boolean;
  item: LegacyNavItem;
  statusSnapshot?: NavStatusSnapshot;
}) {
  const isNotifications =
    item.name === 'Notifications' || item.statusKey === NAV_STATUS_KEY.UNREAD_NOTIFICATIONS;
  const unreadCount = statusSnapshot?.unreadCount ?? 0;

  if (isNotifications) {
    return <NotificationsBadge count={unreadCount} dot={dot} />;
  }

  const statusVariant = resolveItemStatus(item, statusSnapshot);
  if (!statusVariant) return null;

  return <StatusDot large={dot} variant={statusVariant} />;
}

interface NavItemLinkProps {
  item: LegacyNavItem;
  isActive: boolean;
  isCollapsed?: boolean;
  leading?: ReactNode;
  onNavigate?: () => void;
  statusSnapshot?: NavStatusSnapshot;
}

function NavItemLink({
  item,
  isActive,
  isCollapsed = false,
  leading,
  onNavigate,
  statusSnapshot,
}: NavItemLinkProps) {
  if (isCollapsed) {
    if (!item.link) return null;
    return (
      <Link
        to={item.link}
        aria-current={isActive ? 'page' : undefined}
        onClick={onNavigate}
        className={getSidebarNavLeafItemClassName({
          layout: SIDEBAR_NAV_ITEM_LAYOUT.FLYOUT,
          state: getSidebarThemeState(isActive),
        })}
      >
        {leading ? <span className="flex shrink-0 items-center text-inherit">{leading}</span> : null}
        <NavLabel>{item.name}</NavLabel>
        <AccessorySlot>
          <NavItemAccessory dot item={item} statusSnapshot={statusSnapshot} />
        </AccessorySlot>
      </Link>
    );
  }

  if (!item.link) return null;

  return (
    <NavigationList.Item asChild state={getNavigationListState(isActive)}>
      <Link to={item.link} aria-current={isActive ? 'page' : undefined} onClick={onNavigate}>
        {leading ? <span className="flex shrink-0 items-center text-inherit">{leading}</span> : null}
        <NavLabel>{item.name}</NavLabel>
        <AccessorySlot>
          <NavItemAccessory item={item} statusSnapshot={statusSnapshot} />
        </AccessorySlot>
      </Link>
    </NavigationList.Item>
  );
}

interface NavItemWithChildrenProps {
  item: LegacyNavItem;
  isActive: (link: string) => boolean;
  isCollapsed?: boolean;
  leading?: ReactNode;
  onNavigate?: () => void;
  statusSnapshot?: NavStatusSnapshot;
}

function NavItemWithChildren({
  item,
  isActive,
  isCollapsed = false,
  leading,
  onNavigate,
  statusSnapshot,
}: NavItemWithChildrenProps) {
  const children = item.children ?? [];
  const isDescendantActive = children.some(
    (child) => Boolean(child.link) && isActive(child.link ?? '')
  );
  const [isOpen, setIsOpen] = useState(() => isDescendantActive);

  // Nested disclosures default collapsed; auto-open when a child route is current.
  useEffect(() => {
    if (isDescendantActive) setIsOpen(true);
  }, [isDescendantActive]);

  if (isCollapsed) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={getSidebarNavLeafItemClassName({
              layout: SIDEBAR_NAV_ITEM_LAYOUT.FLYOUT,
              // Disclosure chrome only — never leaf-active rail when a child is current
              state: getSidebarThemeState(false),
            })}
            aria-current={isDescendantActive ? 'true' : undefined}
          >
            {leading ? <span className="flex shrink-0 items-center text-inherit">{leading}</span> : null}
            <NavLabel>{item.name}</NavLabel>
            <AccessorySlot>
              <ChevronDown className={sidebarChevron()} />
            </AccessorySlot>
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" sideOffset={8} className="z-input w-48 rounded-lg p-1">
          <div className="px-2 py-1.5">
            <span className={sidebarSectionHeading()}>{item.name}</span>
          </div>
          <div className={cn(sidebarNestedContainer(), 'py-1')}>
            {children.map((child) => (
              <NavItemLink
                key={child.name}
                item={child}
                isActive={Boolean(child.link) && isActive(child.link ?? '')}
                isCollapsed
                leading={getProductsZoneLeading(child)}
                onNavigate={onNavigate}
                statusSnapshot={statusSnapshot}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <NavigationList.Item asChild state={NAVIGATION_LIST_ITEM_STATE.DEFAULT}>
        <CollapsibleTrigger
          className="w-full cursor-pointer"
          aria-current={isDescendantActive ? 'true' : undefined}
        >
          {leading ? <span className="flex shrink-0 items-center text-inherit">{leading}</span> : null}
          <NavLabel>{item.name}</NavLabel>
          <AccessorySlot>
            <ChevronDown className={sidebarChevron({ state: isOpen ? 'open' : 'default' })} />
          </AccessorySlot>
        </CollapsibleTrigger>
      </NavigationList.Item>
      <CollapsibleContent>
        <NavigationList.Group>
          {children.map((child) => (
            <NavItemLink
              key={child.name}
              item={child}
              isActive={Boolean(child.link) && isActive(child.link ?? '')}
              leading={getProductsZoneLeading(child)}
              onNavigate={onNavigate}
              statusSnapshot={statusSnapshot}
            />
          ))}
        </NavigationList.Group>
      </CollapsibleContent>
    </Collapsible>
  );
}

function NavItemEntry({
  item,
  isActive,
  isCollapsed,
  onNavigate,
  statusSnapshot,
}: {
  item: LegacyNavItem;
  isActive: (link: string) => boolean;
  isCollapsed?: boolean;
  onNavigate?: () => void;
  statusSnapshot?: NavStatusSnapshot;
}) {
  const leading = getProductsZoneLeading(item);

  if ((item.children?.length ?? 0) > 0) {
    return (
      <NavItemWithChildren
        item={item}
        isActive={isActive}
        isCollapsed={isCollapsed}
        leading={leading}
        onNavigate={onNavigate}
        statusSnapshot={statusSnapshot}
      />
    );
  }

  if (!item.link) {
    return null;
  }

  return (
    <NavItemLink
      item={item}
      isActive={isActive(item.link)}
      isCollapsed={isCollapsed}
      leading={leading}
      onNavigate={onNavigate}
      statusSnapshot={statusSnapshot}
    />
  );
}

interface ExpandedSectionProps {
  isActive: (link: string) => boolean;
  items: LegacyNavItem[];
  onNavigate?: () => void;
  statusSnapshot?: NavStatusSnapshot;
  title: string;
}

function ExpandedSection({
  isActive,
  items,
  onNavigate,
  statusSnapshot,
  title,
}: ExpandedSectionProps) {
  const headingId = useId();

  return (
    <div className={sidebarSection()}>
      <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV}>
        <NavigationList.Heading id={headingId}>{title}</NavigationList.Heading>
        <NavigationList.Group aria-labelledby={headingId}>
          {items.map((item) => (
            <NavItemEntry
              key={item.name}
              item={item}
              isActive={isActive}
              onNavigate={onNavigate}
              statusSnapshot={statusSnapshot}
            />
          ))}
        </NavigationList.Group>
      </NavigationList.Root>
    </div>
  );
}

interface CollapsedSectionProps {
  icon: ReactNode;
  isActive: (link: string) => boolean;
  items: LegacyNavItem[];
  onNavigate?: () => void;
  statusSnapshot?: NavStatusSnapshot;
  title: string;
}

function isNavTreeActive(items: LegacyNavItem[], isActive: (link: string) => boolean): boolean {
  for (const item of items) {
    if (item.link && isActive(item.link)) {
      return true;
    }
    if (item.children && item.children.length > 0 && isNavTreeActive(item.children, isActive)) {
      return true;
    }
  }
  return false;
}

function CollapsedSection({
  icon,
  isActive,
  items,
  onNavigate,
  statusSnapshot,
  title,
}: CollapsedSectionProps) {
  const headingId = useId();
  const sectionActive = isNavTreeActive(items, isActive);

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={sidebarIconButton({
                state: sectionActive ? 'active' : 'default',
              })}
            >
              {icon}
              <span className="sr-only">{title}</span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{title}</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" sideOffset={8} className="z-input w-56 rounded-lg p-1">
        {/* Whitespace only (P-GAP-SEPARATOR) — no hairline under flyout title */}
        <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV}>
          <NavigationList.Heading id={headingId}>{title}</NavigationList.Heading>
          <NavigationList.Group aria-labelledby={headingId} className="py-1">
            {items.map((item) => (
              <NavItemEntry
                key={item.name}
                item={item}
                isActive={isActive}
                isCollapsed
                onNavigate={onNavigate}
                statusSnapshot={statusSnapshot}
              />
            ))}
          </NavigationList.Group>
        </NavigationList.Root>
      </PopoverContent>
    </Popover>
  );
}

const NON_DASHBOARD_SECTION_TITLE = 'Acquisition';

function getPrimaryPinLeading(item: LegacyNavItem): ReactNode {
  if (item.link === '/home') {
    return <Home className={sidebarIcon()} aria-hidden />;
  }
  if (item.link === '/board/attribution') {
    return <Activity className={sidebarIcon()} aria-hidden />;
  }
  if (item.link?.startsWith('/board/traffic')) {
    return <LineChart className={sidebarIcon()} aria-hidden />;
  }
  if (item.link === '/board/realtime') {
    return <Radio className={sidebarIcon()} aria-hidden />;
  }
  return null;
}

function listPrimaryPinRows(item: LegacyNavItem): LegacyNavItem[] {
  const childPins = (item.children ?? []).filter(
    child => Boolean(child.link) && child.link !== item.link
  );
  if (!item.link) {
    return childPins;
  }
  return [item, ...childPins];
}

function PrimaryPinsBand({
  isActive,
  isCollapsed,
  items,
  onNavigate,
}: {
  isActive: (link: string) => boolean;
  isCollapsed: boolean;
  items: LegacyNavItem[];
  onNavigate?: () => void;
}) {
  if (items.length === 0) return null;

  if (isCollapsed) {
    return (
      <>
        {items.flatMap(item => listPrimaryPinRows(item)).map(pin => {
          if (!pin.link) {
            return null;
          }
          const itemActive = isActive(pin.link);
          return (
            <Tooltip key={pin.name}>
              <TooltipTrigger asChild>
                <Link
                  to={pin.link}
                  onClick={onNavigate}
                  aria-label={pin.name}
                  aria-current={itemActive ? 'page' : undefined}
                  className={sidebarIconButton({
                    state: itemActive ? 'active' : 'default',
                  })}
                >
                  {getPrimaryPinLeading(pin) ?? <Activity className={sidebarIcon()} aria-hidden />}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{pin.name}</TooltipContent>
            </Tooltip>
          );
        })}
      </>
    );
  }

  return (
    <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV} aria-label="Primary">
      <NavigationList.Group>
        {items.flatMap(item => listPrimaryPinRows(item)).map(pin => (
          <NavItemLink
            key={pin.name}
            item={pin}
            isActive={Boolean(pin.link) && isActive(pin.link ?? '')}
            leading={getPrimaryPinLeading(pin)}
            onNavigate={onNavigate}
          />
        ))}
      </NavigationList.Group>
    </NavigationList.Root>
  );
}

export function NonDashboardLink({
  isCollapsed,
  onNavigate,
}: {
  isCollapsed: boolean;
  onNavigate?: () => void;
}) {
  const currentAccount = useCurrentAccount();
  const location = useLocation();
  const accountFeatureFlagNames = currentAccount?.featureFlags?.map((flag) => flag.name) ?? [];
  const items = getNonDashboardItems({ hasShopifyData: true, accountFeatureFlagNames });

  if (items.length === 0) return null;

  // Single-leaf band: no section Heading (avoids N09 duplicate "Acquisition" / leaf).
  // Collapsed: icon button like Primary Views hub — not a CollapsedSection with repeated title.
  if (isCollapsed) {
    const item = items[0];
    if (!item?.link) return null;
    const active = isNavLinkActive(location.pathname, item.link);
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.link}
            onClick={onNavigate}
            aria-label={item.name}
            aria-current={active ? 'page' : undefined}
            className={sidebarIconButton({ state: active ? 'active' : 'default' })}
          >
            <Contact className={sidebarIcon()} aria-hidden />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{item.name}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className={sidebarSection()}>
      <NavigationList.Root as={NAVIGATION_LIST_ROOT_AS.DIV} aria-label={NON_DASHBOARD_SECTION_TITLE}>
        <NavigationList.Group>
          {items.map((item) => (
            <NavItemLink
              key={item.name}
              item={item}
              isActive={Boolean(item.link) && isNavLinkActive(location.pathname, item.link ?? '')}
              leading={<Contact className={sidebarIcon()} />}
              onNavigate={onNavigate}
            />
          ))}
        </NavigationList.Group>
      </NavigationList.Root>
    </div>
  );
}

function SidebarSectionSkeleton({
  headingWidthClassName,
  rowWidthClassNames,
}: {
  headingWidthClassName: string;
  rowWidthClassNames: string[];
}) {
  return (
    <div className={sidebarSection()}>
      <div className="flex flex-col gap-3 px-2">
        <Skeleton className={cn('h-3 rounded-sm', headingWidthClassName)} />
        <div className="flex flex-col gap-2">
          {rowWidthClassNames.map(rowWidthClassName => (
            <Skeleton key={rowWidthClassName} className={cn('h-9 rounded-lg', rowWidthClassName)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SidebarWarmCacheSkeleton({ isCollapsed }: { isCollapsed: boolean }) {
  if (isCollapsed) {
    return (
      <>
        <SidebarContent
          className={cn(sidebarContent({ collapsed: true }), 'bg-surface-navigation')}
          data-testid="app-sidebar-warm-cache-skeleton"
        >
          <div className="flex flex-col items-center gap-3 px-2 py-2">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </SidebarContent>
        <SidebarFooter
          className={cn(sidebarFooter({ collapsed: true }), 'bg-surface-navigation')}
        >
          <div className="flex flex-col items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
        </SidebarFooter>
        <SidebarRail />
      </>
    );
  }

  return (
    <>
      <SidebarContent
        className={cn(sidebarContent({ collapsed: false }), 'bg-surface-navigation')}
        data-testid="app-sidebar-warm-cache-skeleton"
      >
        <SidebarSectionSkeleton
          headingWidthClassName="w-24"
          rowWidthClassNames={['w-full', 'w-4/5']}
        />
        <SidebarSectionSkeleton
          headingWidthClassName="w-20"
          rowWidthClassNames={['w-full', 'w-11/12', 'w-3/4']}
        />
        <SidebarSectionSkeleton
          headingWidthClassName="w-16"
          rowWidthClassNames={['w-5/6', 'w-2/3']}
        />
      </SidebarContent>
      <SidebarFooter className={cn(sidebarFooter({ collapsed: false }), 'bg-surface-navigation')}>
        <div className="flex flex-col gap-2 px-2">
          <Skeleton className="h-9 w-full rounded-lg" />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { state: sidebarState, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isCollapsed = sidebarState === 'collapsed';
  const { currentAccount, hasLoadedAccounts } = useAccountState();
  const { hasShopifyData } = useHasShopifyData();
  const { primaryPins, reports } = getDashboardNavZones(hasShopifyData);
  const statusSnapshot = useNavStatusSnapshot();
  const showWarmCacheSkeleton = Boolean(currentAccount) && !hasLoadedAccounts;

  const isActive = (link: string) => isNavLinkActive(location.pathname, link);

  const handleNavigate = () => {
    if (isMobile) {
      toggleSidebar();
    }
  };

  return (
    <Sidebar collapsible={SIDEBAR_COLLAPSIBLE.ICON}>
      <SidebarHeader className={cn(sidebarHeader(), 'bg-surface-navigation')}>
        <WorkspaceSwitcher />
      </SidebarHeader>

      {showWarmCacheSkeleton ? <SidebarWarmCacheSkeleton isCollapsed={isCollapsed} /> : null}

      {!showWarmCacheSkeleton ? (
        <>
          {/*
        Model A / Stripe zone grammar (R14): Primary (unlabeled) → Shortcuts
        (Saved views) → Products (Reports / Acquisition / System).         Home owns
        the leading Primary destination; Summary and Views follow, then Attribution.
        Zone wrappers are direct SidebarContent children so gap-11 applies between
        bands only — primary pins + Summary + Views hub share Primary; Saved views is next sibling.
        Section labels are P-SECTION-LABEL Headings (never collapsible); only
        item rows disclose (R15 / R17).
      */}
          <SidebarContent className={cn(sidebarContent({ collapsed: isCollapsed }), 'bg-surface-navigation')}>
            {/* Zone A: Primary — Home → Summary → Views hub → Attribution */}
            <div className={sidebarSection()}>
              <PrimaryPinsBand
                items={primaryPins.filter(item => item.link === '/home')}
                isCollapsed={isCollapsed}
                isActive={isActive}
                onNavigate={handleNavigate}
              />
              <OverviewSummaryPin isCollapsed={isCollapsed} />
              <OverviewViewsHub isCollapsed={isCollapsed} />
              <PrimaryPinsBand
                items={primaryPins.filter(item => item.link !== '/home')}
                isCollapsed={isCollapsed}
                isActive={isActive}
                onNavigate={handleNavigate}
              />
            </div>

            {/* Zone B: Shortcuts — Saved views chrome only (hidden when collapsed) */}
            {!isCollapsed ? (
              <div className={sidebarSection()}>
                <OverviewSavedViewsSection />
              </div>
            ) : null}

            {/* Zone C: Reports (P&L + boards; former Finance + Growth) */}
            {reports.length > 0 ? (
              isCollapsed ? (
                <CollapsedSection
                  title={REPORTS_SECTION_TITLE}
                  icon={<BarChart3 className={sidebarIcon()} aria-hidden />}
                  items={reports}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                  statusSnapshot={statusSnapshot}
                />
              ) : (
                <ExpandedSection
                  title={REPORTS_SECTION_TITLE}
                  items={reports}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                  statusSnapshot={statusSnapshot}
                />
              )
            ) : null}

            {/* Zone: Acquisition (gated; distinct from Reports overflow "More") */}
            <NonDashboardLink isCollapsed={isCollapsed} onNavigate={handleNavigate} />

            {/* Zone C: System (former Control Panel; KEEP status dots/badges) */}
            {isCollapsed ? null : (
              <div className="flex flex-col gap-2">
                <ExpandedSection
                  title={SYSTEM_SECTION_TITLE}
                  items={SETUP_ITEMS}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                  statusSnapshot={statusSnapshot}
                />
              </div>
            )}
          </SidebarContent>

          <SidebarFooter
            className={cn(sidebarFooter({ collapsed: isCollapsed }), 'bg-surface-navigation')}
          >
            {isCollapsed ? (
              <div className="flex flex-col items-center gap-1">
                <CollapsedSection
                  title={SYSTEM_SECTION_TITLE}
                  icon={<Settings className={sidebarIcon()} aria-hidden />}
                  items={SETUP_ITEMS}
                  isActive={isActive}
                  onNavigate={handleNavigate}
                  statusSnapshot={statusSnapshot}
                />
                <ApiKeysLink isCollapsed />
              </div>
            ) : (
              <ApiKeysLink isCollapsed={false} />
            )}
          </SidebarFooter>

          <SidebarRail />
        </>
      ) : null}
    </Sidebar>
  );
}
